'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';

const arrowClassName =
  'absolute z-[1] h-11 w-11 -translate-y-1/2 cursor-pointer rounded-full border-[1.5px] border-[#e6eaee] bg-white text-[1.1rem] shadow-[0_8px_20px_-10px_rgba(0,0,0,.35)] transition-all duration-200 hover:border-[#14181c] hover:bg-[#14181c] hover:text-white disabled:pointer-events-none disabled:opacity-0';

export type ScrollState = { previous: boolean; next: boolean };

/**
 * A horizontal scroller paged by arrows. `canScroll` tells which way there is more to see; `scroll`
 * moves by whole items, so the item cut off at the edge becomes the first one fully visible. The
 * items are the scroller's children, the scroller must be positioned (they are measured by
 * `offsetLeft`) and its `scroll-padding` keeps them clear of the arrows.
 */
export function useScrollArrows<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [canScroll, setCanScroll] = useState<ScrollState>({ previous: false, next: false });

  useEffect(() => {
    const scroller = ref.current;
    if (!scroller) return;

    const update = () => {
      const previous = scroller.scrollLeft > 1;
      const next = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
      setCanScroll(current =>
        current.previous === previous && current.next === next ? current : { previous, next },
      );
    };
    // The items too: their width changes without the scroller's, e.g. when the web font loads.
    const observer = new ResizeObserver(update);

    update();
    observer.observe(scroller);
    Array.from(scroller.children).forEach(item => observer.observe(item));
    scroller.addEventListener('scroll', update, { passive: true });

    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', update);
    };
  }, []);

  const scroll = (direction: -1 | 1) => {
    const scroller = ref.current;
    if (!scroller) return;

    const style = getComputedStyle(scroller);
    const paddingStart = Number.parseFloat(style.scrollPaddingLeft) || 0;
    const paddingEnd = Number.parseFloat(style.scrollPaddingRight) || 0;
    const viewStart = scroller.scrollLeft + paddingStart;
    const viewEnd = scroller.scrollLeft + scroller.clientWidth - paddingEnd;
    const items = Array.from(scroller.children) as HTMLElement[];

    if (direction > 0) {
      const item = items.find(entry => entry.offsetLeft + entry.offsetWidth > viewEnd + 1);
      scroller.scrollTo({
        left: item ? item.offsetLeft - paddingStart : scroller.scrollWidth,
        behavior: 'smooth',
      });
    } else {
      const item = items.findLast(entry => entry.offsetLeft < viewStart - 1);
      scroller.scrollTo({
        left: item ? item.offsetLeft + item.offsetWidth + paddingEnd - scroller.clientWidth : 0,
        behavior: 'smooth',
      });
    }
  };

  return { ref, canScroll, scroll };
}

type ScrollArrowsProps = {
  canScroll: ScrollState;
  onScroll: (direction: -1 | 1) => void;
  previousLabel: string;
  nextLabel: string;
  /** Vertical position of both arrows, e.g. `top-1/2`. */
  className: string;
};

/** ← → over the edges of a scroller; an arrow fades out when there is nothing more that way. */
export function ScrollArrows({
  canScroll,
  onScroll,
  previousLabel,
  nextLabel,
  className,
}: ScrollArrowsProps) {
  return (
    <>
      <button
        type="button"
        aria-label={previousLabel}
        disabled={!canScroll.previous}
        onClick={() => onScroll(-1)}
        className={cn(arrowClassName, className, '-left-3')}
      >
        ←
      </button>
      <button
        type="button"
        aria-label={nextLabel}
        disabled={!canScroll.next}
        onClick={() => onScroll(1)}
        className={cn(arrowClassName, className, '-right-3')}
      >
        →
      </button>
    </>
  );
}
